import { Icon } from '@iconify/react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { showMessage } from 'utils/snackbar';
import * as Yup from 'yup';
import { UserService } from '_apis_/user';
import useAuth from '../../../hooks/useAuth';

// ----------------------------------------------------------------------
type InitialValues = {
  email: string;
  password: string;
  remember: boolean;
  message?: string;
};

export interface LoginFormProps {
  headingChange: any;
}
export default function LoginForm({ headingChange }: LoginFormProps) {
  const { login } = useAuth();
  const userService = new UserService();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [recoverPasswordMode, setRecoverPasswordMode] = useState<boolean>(false);

  const handleForgotPasswordMode = (show: boolean) => {
    setRecoverPasswordMode(!show);
    headingChange(!show);
  };

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    ...(!recoverPasswordMode && {
      password: Yup.string().required('Password is required')
    })
  });

  const handleError = (error, setErrors) => {
    const message: string =
      error === 'Network Error'
        ? 'Check your internet connection'
        : error.data && error.data.messages;
    setErrors({
      message
    });
  };

  const submitLogin = async (values, setErrors, setSubmitting) => {
    try {
      const { email, password } = values;
      const emailFormat: string = email.toLowerCase();
      await login({ email: emailFormat, password });
      showMessage('Login success', 'success');
    } catch (error) {
      setSubmitting(false);
      handleError(error, setErrors);
    }
  };
  const submitRecoverPassword = async (values, setErrors, setSubmitting) => {
    try {
      const response = await userService.forgotPassword({ ...values });
      const message = response.data?.description || response.data?.Result;
      if (response && response.status >= 200 && response.status <= 299) {
        showMessage(message, 'success');
        setRecoverPasswordMode(false);
        headingChange(false);
        setSubmitting(false);
      }
    } catch (error) {
      handleError(error, setErrors);
    }
  };

  const formik = useFormik<InitialValues>({
    initialValues: {
      email: '',
      password: '',
      remember: true
    },
    validationSchema: LoginSchema,
    onSubmit: (values, { setErrors, setSubmitting }) => {
      try {
        setSubmitting(true);
        if (recoverPasswordMode) {
          submitRecoverPassword(values, setErrors, setSubmitting);
        } else {
          submitLogin(values, setErrors, setSubmitting);
        }
      } catch {
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit} title={'Login'}>
        <Stack spacing={3}>
          {errors.message && !recoverPasswordMode && (
            <Alert data-testid="error-login-message" severity="error">
              {errors.message}
            </Alert>
          )}
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            autoComplete="username"
            type="email"
            label="Email address"
            inputProps={{
              'data-testid': 'email-recovery'
            }}
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          {!recoverPasswordMode && (
            <TextField
              fullWidth
              autoComplete="current-password"
              InputLabelProps={{ shrink: true }}
              type={showPassword ? 'text' : 'password'}
              label="Password"
              {...getFieldProps('password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Icon icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
            />
          )}
        </Stack>
        {!recoverPasswordMode && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
            <FormControlLabel
              control={<Checkbox {...getFieldProps('remember')} checked={values.remember} />}
              label="Remember me"
            />
          </Stack>
        )}

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          data-testid="login-btn"
          sx={{ marginTop: 1 }}
        >
          {recoverPasswordMode ? `Submit` : `Login`}
        </LoadingButton>
      </Form>
      <Link
        onClick={() => handleForgotPasswordMode(recoverPasswordMode)}
        sx={{ paddingTop: 3, textAlign: 'center', cursor: 'pointer' }}
        underline="none"
        variant="subtitle2"
        data-testid="option-link"
      >
        {recoverPasswordMode ? `Back to sign in` : `Forgot your password`}
      </Link>
    </FormikProvider>
  );
}
